/*
 * The composer's chrome (F-2 §3.2, X-A3 P1/P2, VISUAL.md §7.6): the box that carries the focus
 * ring, the text a person writes on its first line, the tool row under that text, the attachment
 * tray between the two, a surface's own row under the tools, the drop overlay inside the box, and —
 * out of flow in the region's own bottom padding — the hint that names the send key, with the
 * lines that refuse a send below it.
 *
 * Input first, tools under it. What a person came to do is write, so the text is the top of the box
 * and the controls sit under it where a hand expects the send to be; the send itself is drawn at
 * rest, inert while there is nothing to send, so the one action the screen is for is visible before
 * anybody has typed. That order is also what keeps the resting region at 97 px: the text line is
 * 36, the tool row 34, and the hint costs nothing because it is painted into padding the region
 * already has.
 *
 * Everything here is the same for every surface, so a second surface is a `children` and a
 * `toolbar` away rather than a second composer — and a person who switches between them sees one
 * box, one send button and one refusal.
 *
 * The hint has three states and the frame knows two of them: what the send setting says. The third
 * belongs to wherever the caret is — inside a list Enter adds an item — and only the surface can see
 * that, so it hands the sentence in (`hint`).
 *
 * Two things the frame does for both surfaces at once, because both are about the box and neither
 * about how text is entered. A surface whose send is more than one thing (`sendAction`) gets its
 * words on the button and a joined menu of the other ways to send, and the menu's chords work in
 * either surface: the box listens for them before the text does. And a paste too long to read in the
 * box (`longPaste`) is taken into the tray as a file before either surface sees it.
 */

import { useRef, type ReactNode } from "react"
import { cx } from "../../lib/cx.ts"
import { Button, Menu, type MenuItem } from "../../ui/index.ts"
import { formatNumber, t, translate, type PlainKey } from "../i18n/index.ts"
import { Icon } from "../shell/index.ts"
import { ariaKeys, matchesKeys, printKeys } from "../shell/shortcuts.ts"
import { AttachmentTray } from "./attachments/AttachmentTray.tsx"
import "./composer.css"
import { noHoverPointer } from "./pointer.ts"
import type { SendAction, SendMenuItem } from "./types.ts"
import { MAX_MESSAGE_CHARS, type ComposerCore } from "./useComposerCore.ts"

/**
 * What a tool row hands a control it did not draw itself, so that its one tab stop reaches it: the
 * attribute the row finds the control by, the roving `tabIndex` and the focus that moves the stop.
 */
export interface ToolStop {
	"data-stop": string
	tabIndex: number
	onFocus: () => void
}

/**
 * The attach control: the button and the hidden input it clicks.
 *
 * It belongs to the frame — every surface offers it and the tray it fills is the frame's — and it is
 * exported so that a surface's own tool row can place it where VISUAL.md §7.6 orders the row (after
 * the emoji, before the `⋯`) and take it into its single tab stop. A surface without such a row gets
 * it from the frame, in the bar.
 */
export function AttachControl({ core, stop }: { core: ComposerCore; stop?: ToolStop }) {
	const filesRef = useRef<HTMLInputElement>(null)
	return (
		<>
			{/* `hidden` rather than the visually-hidden class: nobody ever reaches this input — the
			    button beside it does the clicking — so a box that is drawn at all is a one-pixel
			    pointer target nothing announces. */}
			<input
				ref={filesRef}
				hidden
				type="file"
				multiple
				tabIndex={-1}
				aria-hidden="true"
				onChange={(event) => {
					core.attachments.attach(Array.from(event.target.files ?? []))
					// The same file picked twice in a row fires no change event unless the input is
					// emptied in between.
					event.target.value = ""
				}}
			/>
			<Button
				quiet
				icon
				size="sm"
				className="tb-composer__tool"
				aria-label={t("composer.attach.button")}
				title={t("composer.attach.button")}
				disabled={core.disabled}
				before={<Icon name="paperclip" />}
				onClick={() => filesRef.current?.click()}
				{...stop}
			/>
		</>
	)
}

/**
 * The send button with words (`ComposerProps.sendAction`): the label says what the press does —
 * *Queue*, *Post note*, *Run /compact* — and a chevron joined to it opens the other ways to send.
 * The label is inert while there is nothing to send; the chevron stays live, because what the other
 * ways are is worth reading before anything is typed — outlined while the label is inert, so an
 * empty box does not show a second primary button beside a grey one.
 */
function SendSlot({ core, action }: { core: ComposerCore; action: SendAction }) {
	const variant = action.variant ?? "primary"
	const busy = core.waitingForUploads || core.attachments.busy
	const hint = busy
		? t("composer.attach.sendWhenReady")
		: action.hintKey === undefined
			? undefined
			: translate(action.hintKey, action.hintArgs)
	const menu = action.menu ?? []
	return (
		<span className="tb-composer__send">
			<Button
				variant={variant}
				size="sm"
				className="tb-composer__submit tb-composer__send-label"
				label={translate(action.labelKey, action.labelArgs)}
				{...(hint === undefined ? {} : { title: hint, "aria-description": hint })}
				disabled={!core.canSend}
				onClick={core.submit}
			/>
			{menu.length === 0 ? null : (
				<Menu
					trigger={
						<Button
							variant={core.canSend ? variant : "secondary"}
							icon
							size="sm"
							className="tb-composer__send-more"
							aria-label={t("composer.send.more")}
							title={t("composer.send.more")}
							before={<Icon name="chevronDown" />}
						/>
					}
					items={menu.map((item) => menuItemOf(core, item))}
				/>
			)}
		</span>
	)
}

/** One send-menu entry as the menu draws it: the label, its line, its first chord. */
function menuItemOf(core: ComposerCore, item: SendMenuItem): MenuItem {
	const keys = item.keys?.[0]
	const detail =
		item.detailKey === undefined ? undefined : translate(item.detailKey, item.detailArgs)
	return {
		id: item.id,
		label: translate(item.labelKey, item.labelArgs),
		...(detail === undefined ? {} : { detail }),
		...(item.icon === undefined ? {} : { icon: item.icon }),
		...(keys === undefined ? {} : { shortcut: printKeys(keys), shortcutAria: ariaKeys(keys) }),
		// An entry is a send: with nothing in the box there is nothing for it to send.
		disabled: item.disabled === true || !core.canSend,
		onSelect: () => core.submitVia(item),
	}
}

/**
 * The send menu's chords, heard before the surface hears the key: a chord an entry claims is the
 * entry's in either surface, and an IME composing a candidate is never interrupted.
 */
function onSendChord(core: ComposerCore, event: React.KeyboardEvent): void {
	const menu = core.sendAction?.menu
	if (menu === undefined || event.nativeEvent.isComposing) return
	const item = menu.find((entry) => entry.keys?.some((spec) => matchesKeys(spec, event)))
	if (item === undefined) return
	event.preventDefault()
	event.stopPropagation()
	core.submitVia(item)
}

/**
 * A paste too long for the box, taken into the tray before the surface inserts it. Stopping it here
 * keeps both surfaces out of it: the textarea's own handler and the editor's native one never see
 * the event.
 */
function onLongPaste(core: ComposerCore, event: React.ClipboardEvent): void {
	const text = event.clipboardData.getData("text/plain")
	if (!core.pasteLong(text)) return
	event.preventDefault()
	event.stopPropagation()
}

export interface ComposerFrameProps {
	core: ComposerCore
	/** A modifier the surface adds to the frame's root — today, "this box folds its format row". */
	className?: string | undefined
	/** The editing surface, on the box's first line, with whatever popups belong to it. */
	children: ReactNode
	/**
	 * The formatting toolbar, on the tool row under the text. It follows the surface in the DOM as it
	 * does on screen (WCAG 2.4.3); the source view has none.
	 */
	toolbar?: ReactNode
	/**
	 * Set by a surface whose `toolbar` draws the attach control itself (with `AttachControl`), so the
	 * frame does not draw a second one. §7.6 orders the row link · emoji · attach, then the `⋯`, and
	 * only the row that owns the roving tab stop can place a control between two of its own.
	 */
	attachInToolbar?: boolean
	/**
	 * The sentence under the box, when the surface knows a better one than the send setting's: the
	 * caret inside a list, where Enter adds an item (X-B1 A7). Absent leaves the frame's two states.
	 */
	hint?: PlainKey | undefined
	/** A pane between the box and the hint — the source view's server-rendered preview. */
	preview?: ReactNode
	/** The first control of the tool row, before the toolbar — the toggle that unfolds it. */
	lead?: ReactNode
	/** Surface-owned controls, at the end of the tool row before Send. */
	actions?: ReactNode
	/**
	 * A surface's own row inside the box under the tools — the thread pane's *Also send to #design*.
	 * It is inside the frame because it is part of what the send does.
	 */
	belowTools?: ReactNode
}

export function ComposerFrame({
	core,
	className,
	children,
	toolbar,
	attachInToolbar,
	hint,
	preview,
	lead,
	actions,
	belowTools,
}: ComposerFrameProps) {
	const { attachments, disabled } = core
	const touch = noHoverPointer()
	const sendLabel =
		core.waitingForUploads || attachments.busy
			? t("composer.attach.sendWhenReady")
			: t("composer.send")

	return (
		<div
			className={cx(
				"tb-composer",
				className,
				disabled && "tb-composer--disabled",
				core.compact && "tb-composer--compact",
				// The hint under the box is chrome a reader needs only while writing: it shows while
				// the box has focus or holds something to send, and the class is what the stylesheet
				// reads. A notice below the box takes its place — two sentences on one line would
				// overlap, and the refusal is the one worth reading.
				!core.empty && "tb-composer--filled",
				(core.tooLong || core.draft.tooLarge || core.failure !== null) && "tb-composer--noticed",
			)}
		>
			{/* The box listens before what is typed into it: the send menu's chords and a long paste
			    are the box's, whichever surface has the caret. */}
			<div
				className="tb-composer__box"
				onKeyDownCapture={(event) => onSendChord(core, event)}
				onPasteCapture={(event) => onLongPaste(core, event)}
			>
				{/* The text line is the surface itself: `.tb-composer__input`, which both surfaces wear,
				    carries the padding. No wrapper around it, so the element a person types into is a
				    child of the box that paints the focus ring — which is what a reader's browser and
				    the accessibility lane both look one level up for. */}
				{children}
				<AttachmentTray tray={attachments.tray} files={attachments.files} />
				<div className="tb-composer__bar">
					{lead}
					{toolbar}
					{/* Attach stands in the tool row; a row of its own places it between the emoji and
					    the `⋯` and this is where every other surface gets it. */}
					{attachInToolbar === true || !core.takesFiles ? null : <AttachControl core={core} />}
					{/* Everything in here takes the end of the row, whatever stands to its left. A flex
					    margin on a wrapper rather than a spacer element: an empty span announces nothing
					    to a screen reader. */}
					<span className="tb-composer__actions">
						{actions}
						{core.cancel ? (
							// An edit is closed by Escape, and by a button as well: a way out that only
							// the keyboard knows about is no way out (spec 08 §4).
							<Button size="sm" label={t("composer.cancel")} onClick={() => core.cancel?.()} />
						) : null}
						{/* Send is drawn from the first frame and is inert until there is something to
						    send: the primary action of the screen is not something a person should have
						    to type to discover (D-05 D14). */}
						{core.sendAction === undefined ? (
							<Button
								variant="primary"
								icon
								size="sm"
								className="tb-composer__submit"
								aria-label={sendLabel}
								title={sendLabel}
								disabled={!core.canSend}
								before={<Icon name="send" />}
								onClick={core.submit}
							/>
						) : (
							<SendSlot core={core} action={core.sendAction} />
						)}
					</span>
				</div>
				{belowTools}
				{attachments.dragging ? (
					// The overlay is what says the application took the drag. Without it the only
					// feedback a drop gives is the browser navigating to the file, which takes the
					// tab — and everything unsent in it — away from the person.
					<div className="tb-composer__dropzone">{t("composer.attach.dropHere")}</div>
				) : null}
			</div>
			{preview}
			{/* The hint is always in the document, because every surface is described by it, and it is
			    drawn only while somebody is writing. A compact box — an edit, a thread reply — keeps
			    the description and never the line: the sentence stands in the shortcut sheet, and a
			    350-px column has no room to spend on it (X-A3 V7).

			    Where the pointer cannot hover there is no line at all: Enter starts a new line there
			    and the send button is what posts, so the sentence about the send key would be wrong
			    as well as in the way. The description stays — it is what `aria-describedby` names —
			    and it says what that device does instead.

			    A sentence the surface hands in wins over the setting's: inside a list Enter adds an
			    item whatever the setting says, and the line is what makes that discoverable. */}
			{touch ? (
				<span id={core.hintId} className="tb-visually-hidden">
					{t("composer.hint.touch")}
				</span>
			) : (
				<span id={core.hintId} className="tb-composer__hint">
					{hint !== undefined
						? t(hint)
						: core.settings.enterSends
							? t("composer.hint.enterSends")
							: t("composer.hint.shiftEnterSends")}
					{/* The box's description, not its chrome: the exception matters to somebody typing a
					    code block and costs a line of the composer to everybody else. */}
					<span className="tb-visually-hidden">{` ${t("composer.hint.codeBlock")}`}</span>
				</span>
			)}
			{core.tooLong ? (
				<p
					id={core.statusId}
					className="tb-composer__status"
					role="status"
					aria-label={t("composer.status.refused")}
				>
					{t("composer.tooLongCount", {
						count: formatNumber(core.value.length),
						max: formatNumber(MAX_MESSAGE_CHARS),
					})}
				</p>
			) : null}
			{core.draft.tooLarge ? (
				<p className="tb-composer__status" role="status" aria-label={t("composer.status.draft")}>
					{t("composer.draft.tooLarge")}
				</p>
			) : null}
			{core.failure !== null ? (
				<p className="tb-composer__error" role="alert" aria-label={t("composer.status.refused")}>
					{t("composer.sendFailed", { reason: translate(core.failure.key, core.failure.args) })}
				</p>
			) : null}
		</div>
	)
}
